import { useApi } from '@classgo/app-kit';
import type { GuardianListItem } from '@classgo/shared';
import { createGuardianInputSchema, updateGuardianInputSchema } from '@classgo/shared';
import { Input, Select } from '@classgo/ui';
import { useQuery } from '@tanstack/react-query';

import { useT } from '../i18n';
import { useForm } from '../lib/form';
import { peopleApi, peopleQueries } from '../people/api';
import { FormShell } from './form-shell';

const text = (value: string) => (value.trim() === '' ? undefined : value.trim());

/**
 * Create or edit a guardian (acudiente); linking to a student is a separate step.
 */
export const GuardianForm = ({
  current,
  onDone,
}: {
  current: Pick<
    GuardianListItem,
    'id' | 'givenName' | 'familyName' | 'email' | 'phone' | 'documentType' | 'documentNumber'
  > | null;
  onDone: (id: string) => void;
}) => {
  const t = useT();
  const api = useApi();
  const pack = useQuery(peopleQueries.countryPack(api));
  const form = useForm({
    schema: current ? updateGuardianInputSchema : createGuardianInputSchema,
    initial: {
      givenName: current?.givenName ?? '',
      familyName: current?.familyName ?? '',
      email: current?.email ?? '',
      phone: current?.phone ?? '',
      documentType: current?.documentType ?? '',
      documentNumber: current?.documentNumber ?? '',
    },
    toInput: (values) =>
      current
        ? {
            givenName: values.givenName,
            familyName: values.familyName,
            email: values.email.trim() === '' ? null : values.email.trim(),
            phone: values.phone.trim() === '' ? null : values.phone.trim(),
            documentType: values.documentType === '' ? null : values.documentType,
            documentNumber:
              values.documentNumber.trim() === '' ? null : values.documentNumber.trim(),
          }
        : {
            givenName: values.givenName,
            familyName: values.familyName,
            email: text(values.email),
            phone: text(values.phone),
            documentType: text(values.documentType),
            documentNumber: text(values.documentNumber),
          },
  });
  const adultTypes = (pack.data?.documentTypes ?? []).filter((type) => !type.forMinors);
  return (
    <FormShell
      error={form.errors.form}
      busy={form.busy}
      onSubmit={(event) => {
        event.preventDefault();
        void form.submit(async (input) => {
          const ref = current
            ? await peopleApi(api).updateGuardian(current.id, input as never)
            : await peopleApi(api).createGuardian(input as never);
          onDone(ref.id);
        });
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label={t('people.field.givenName')}
          required
          value={form.values.givenName}
          onChange={(event) => form.set('givenName', event.target.value)}
          {...(form.fieldError('givenName') ? { error: form.fieldError('givenName') } : {})}
        />
        <Input
          label={t('people.field.familyName')}
          required
          value={form.values.familyName}
          onChange={(event) => form.set('familyName', event.target.value)}
          {...(form.fieldError('familyName') ? { error: form.fieldError('familyName') } : {})}
        />
        <Input
          label={t('people.field.email')}
          type="email"
          help={t('guardian.field.email.help')}
          value={form.values.email}
          onChange={(event) => form.set('email', event.target.value)}
          {...(form.fieldError('email') ? { error: form.fieldError('email') } : {})}
        />
        <Input
          label={t('people.field.phone')}
          value={form.values.phone}
          onChange={(event) => form.set('phone', event.target.value)}
          {...(form.fieldError('phone') ? { error: form.fieldError('phone') } : {})}
        />
        <Select
          label={t('people.field.documentType')}
          value={form.values.documentType}
          onChange={(event) => form.set('documentType', event.target.value)}
          options={[
            { value: '', label: t('common.none') },
            ...adultTypes.map((type) => ({
              value: type.code,
              label: `${type.code} · ${type.name}`,
            })),
          ]}
          {...(form.fieldError('documentType') ? { error: form.fieldError('documentType') } : {})}
        />
        <Input
          label={t('people.field.documentNumber')}
          value={form.values.documentNumber}
          onChange={(event) => form.set('documentNumber', event.target.value)}
          {...(form.fieldError('documentNumber')
            ? { error: form.fieldError('documentNumber') }
            : {})}
        />
      </div>
    </FormShell>
  );
};
